import { useReactiveVar } from '@apollo/client'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
    useEditorActionBarActions,
    useHasHydrated,
} from '../store/EditorActionBarStore'

/**
 * Handles bidirectional sync of isInactiveContentVisible with old workspace's localStorage.
 * - Reads from localStorage after hydration (old → new)
 * - Returns function to write to localStorage (new → old)
 */
export const useSyncInactiveContentVisibility = () => {
    const {
        syncInactiveContentVisibilityFromLocalStorage,
        syncInactiveContentVisibilityToLocalStorage,
    } = useEditorActionBarActions()

    const hasHydrated = useHasHydrated()
    const { projectId } = useParams<{ projectId?: string }>()
    const { selectedElementNode } = useReactiveVar(workspaceSelectedElement)
    const {
        currentUserInfo: { kpUserId: userId },
    } = useReactiveVar(userInfo)

    // Read from localStorage after hydration
    useEffect(() => {
        if (
            hasHydrated &&
            userId &&
            projectId &&
            selectedElementNode?.id &&
            selectedElementNode?.baseElementId
        ) {
            syncInactiveContentVisibilityFromLocalStorage({
                userId,
                projectId,
                elementId: selectedElementNode.id,
                baseElementId: selectedElementNode.baseElementId,
            })
        }
    }, [
        hasHydrated,
        userId,
        projectId,
        selectedElementNode?.id,
        selectedElementNode?.baseElementId,
        syncInactiveContentVisibilityFromLocalStorage,
    ])

    const syncToLocalStorage = useCallback(
        (value: boolean) => {
            if (userId && projectId && selectedElementNode) {
                syncInactiveContentVisibilityToLocalStorage({
                    userId,
                    projectId,
                    elementId: selectedElementNode.id,
                    baseElementId: selectedElementNode.baseElementId,
                    value,
                })
            }
        },
        [
            userId,
            projectId,
            selectedElementNode,
            syncInactiveContentVisibilityToLocalStorage,
        ]
    )

    return { syncToLocalStorage }
}
